import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import {
  JobsListTypes,
  RecurranceType,
  WorkflowStates,
} from '../../constants/job-detail.constants';
import {
  RecurringJob,
  RecurringJobsResult,
} from '../../models/job-detail/details/recurring-jobs-response';
import { TradeeService } from 'src/app/core/services/http/trade-mgmt/trade.service';
import { SettingsManagementService } from 'src/app/core/services/http/agency-mgmt/settings-management.service';
import { PaymentSettings } from 'src/app/settings/models/payment-settings.model';
import { JobService } from 'src/app/core/services/http/maintenance/job.service';
import { JobOverviewResult } from '../../models/job-detail/overview/job-detail-overview-response.model';
import { Job } from '../../models/job-post/job.model';

@Injectable({
  providedIn: 'any',
})
export class JobDetailService {
  private propertyId: string;
  private jobId: string;
  private jobType: JobsListTypes;
  private actionableJobSubject$ = new BehaviorSubject<string>(null);
  actionableJobChanged$ = this.actionableJobSubject$.asObservable();

  constructor(
    private tradeSvc: TradeeService,
    private settingSvc: SettingsManagementService,
    private jobService: JobService
  ) { }

  getPropertyId() {
    return this.propertyId;
  }
  setJobId(jobId: string) {
    this.actionableJobSubject$.next(jobId);
  }

  invalidate() {
    this.actionableJobSubject$.next(null);
  }

  getCurrentJobId() {
    return this.jobId;
  }
  getCurrentJobType() {
    return this.jobType;
  }
  getMappedRecurrenceJobs(result: RecurringJobsResult): RecurringJobsResult {
    result.recurringJobs.forEach((job) => {
      job.statusDisplay = this.getStatusDisplay(job);
      if (job?.scheduleStartDate)
        job.scheduleStartDate = this.addHours(
          job.scheduleStartDate,
          result.defaultTimeZoneDiffMinutes
        );
      if (job?.scheduleEndDate)
        job.scheduleEndDate = this.addHours(
          job.scheduleEndDate,
          result.defaultTimeZoneDiffMinutes
        );
    });
    return result;
  }

  private addHours(date, hours): Date {
    var result = new Date(date);
    result.setHours(result.getHours() + hours / 60);
    return result;
  }

  private getStatusDisplay(job: RecurringJob): string {
    switch (job?.status) {
      case WorkflowStates.JobDrafted:
        return 'Job Drafted';
      case WorkflowStates.JobPosted:
        return 'Quotes Pending';
      case WorkflowStates.JobQuotesAvailable:
        return job.numberOfQuotes > 0 ? 'Quote Provided' : 'Quotes Available';
      case WorkflowStates.JobQuoteAccepted:
        return 'Awarded';
      case WorkflowStates.JobAwaitingForApproval:
        return 'Job Awaiting Approval';
      case WorkflowStates.JobCancelled:
        return 'Job Cancelled';
      case WorkflowStates.JobAwaitingOwnerApproval:
        return 'Job Awaiting Approval (Owner)';
      case WorkflowStates.JobCompleted:
        return 'Job Completed';
      case WorkflowStates.JobRejected:
        return 'Job Rejected';
      case WorkflowStates.JobApprovedByOwner:
        return 'Owner Approved';
      case WorkflowStates.JobRejectedByOwner:
        return 'Job Rejected by Owner';
      case WorkflowStates.JobPendingReview:
        return job.isPendingPayment
          ? 'Pending Service Provider Payment'
          : job.readyForMonthlyPayment
            ? 'Monthly Payment'
            : 'Invoice Review';
      case WorkflowStates.JobAwardPendingApproval:
        return 'Job Award Awaiting Approval';
      case WorkflowStates.JobPostedForThirdParty:
        return 'Job Posted For Third Party';
      case WorkflowStates.JobCeased:
        return 'Job Ceased';
      case WorkflowStates.JobArchived:
        return 'Job Award Rejected';
      case WorkflowStates.JobPendingInvoice:
        return 'Job Pending for Invoice';
      case WorkflowStates.Inactive:
        return 'Inactive';
      case WorkflowStates.BidRejected:
        return 'Quote Rejected';
      case WorkflowStates.JobCompletedBySubcontractor:
        return 'Invoice Pending Review';
      default:
        return '';
    }
  }

  validateLicenceInsurance(primaryJobTypeId: string, tradeIdList: string[]) {
    return this.tradeSvc
      .validateLicenceInsurance(primaryJobTypeId, tradeIdList)
      .pipe(
        tap((licence) => {
          if (this.isJobPostFlow()) {
            this.allowTradeeForLitePm(licence);
          } else {
            this.jobService.getOverview().subscribe({
              next: (jobOverviewResult: JobOverviewResult) => {
                if (
                  jobOverviewResult?.type == JobsListTypes.DraftedJobs ||
                  jobOverviewResult?.type == JobsListTypes.QuotedJobs ||
                  jobOverviewResult?.type == JobsListTypes.PostedJobs
                ) {
                  this.allowTradeeForLitePm(licence);
                }
              },
            });
          }
        })
      );
  }

  isLitePmAndTradeVerificationEnabled(paymentSettings) {

    if (!paymentSettings.isPremiumPackageEnabled) {
      let addOnTradeVerificationservice: any = paymentSettings.addonList.filter(
        (x) => x.name == 'Trade verification service'
      );
      addOnTradeVerificationservice = addOnTradeVerificationservice.length > 0 ? addOnTradeVerificationservice[0] : null;
      if (
        addOnTradeVerificationservice &&
        !addOnTradeVerificationservice.isSelected
      ) {
        return true;
      }
    }
    return false;
  }

  isJobPostFlow() {
    return Job.getInstance()?.currentStep > 0;
  }

  allowTradeeForLitePm(licence) {
    this.settingSvc.getPaymentSettings().subscribe({
      next: (response: PaymentSettings) => {
        if (this.isLitePmAndTradeVerificationEnabled(response)) {
          licence.isAllowedToPlaceQuote = true;
        }
      },
    });
  }

  filterRecurrenceTypeForRecurrenceEndDateVisibility(jobRecurrencyType: RecurranceType) {
    return RecurranceType.None != jobRecurrencyType;
  }

}
